/**
 * Iter 67v — Customer Loyalty / Rewards tab.
 *
 * Hybrid points + tier system. Renders six stacked sections:
 *   1. Tier progress card (current Bronze/Silver/Gold + progress bar)
 *   2. Points balance + redeem value
 *   3. Active perks unlocked
 *   4. Referral share card (personal code + copy + WhatsApp/Email/SMS share)
 *   5. Earnings ledger (last 30 rows)
 *   6. "How it works" explainer
 *
 * All data sourced from /api/customer/loyalty + /api/customer/loyalty/transactions.
 */
import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Trophy, Sparkles, Gift, Users2, Copy, Share2, ArrowRight, Check, Mail,
  MessageCircle, Crown, Shield, Award, Coins, BookOpen, Receipt,
  Flame, Star, BadgePercent,
} from "lucide-react";

const TIER_VISUALS = {
  bronze: { ring: "ring-amber-300", grad: "from-amber-400 to-orange-500",
            chip: "bg-amber-50 text-amber-800 border-amber-200", icon: Award },
  silver: { ring: "ring-slate-300", grad: "from-slate-400 to-slate-600",
            chip: "bg-slate-100 text-slate-800 border-slate-300", icon: Shield },
  gold:   { ring: "ring-yellow-300", grad: "from-yellow-400 via-amber-400 to-orange-500",
            chip: "bg-yellow-50 text-yellow-800 border-yellow-300", icon: Crown },
};

const TX_TONE = {
  earn:     { bg: "bg-emerald-50", text: "text-emerald-700", ring: "ring-emerald-200", icon: "Coins" },
  spend:    { bg: "bg-rose-50",    text: "text-rose-700",    ring: "ring-rose-200",    icon: "BadgePercent" },
  referral: { bg: "bg-indigo-50",  text: "text-indigo-700",  ring: "ring-indigo-200",  icon: "Users2" },
  adjust:   { bg: "bg-slate-50",   text: "text-slate-700",   ring: "ring-slate-200",   icon: "Sparkles" },
};

const TX_ICON_LOOKUP = { Coins, BadgePercent, Users2, Sparkles };

export default function LoyaltyTab() {
  const [data, setData] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, t] = await Promise.all([
        api.get("/customer/loyalty"),
        api.get("/customer/loyalty/transactions"),
      ]);
      setData(s.data);
      setTransactions(t.data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const copyCode = async () => {
    if (!data?.referral_code) return;
    try {
      await navigator.clipboard.writeText(data.referral_code);
      setCopied(true);
      toast.success("Referral code copied.");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Couldn't copy — long-press to select instead.");
    }
  };

  if (loading || !data) {
    return <div className="text-sm text-neutral-500" data-testid="loyalty-loading">Loading rewards…</div>;
  }

  const tier = data.tier;
  const tv = TIER_VISUALS[tier.key] || TIER_VISUALS.bronze;
  const TierIcon = tv.icon;
  const progress = data.next_tier_progress;
  const pct = Math.round((progress?.ratio || (tier.key === "gold" ? 1 : 0)) * 100);
  const referralUrl = typeof window !== "undefined"
    ? `${window.location.origin}/register?ref=${encodeURIComponent(data.referral_code)}`
    : `/register?ref=${data.referral_code}`;
  const shareMsg = encodeURIComponent(
    `Renting a car? Get €10 off your first RentalCar.al trip with my code ${data.referral_code} → ${referralUrl}`
  );

  return (
    <div className="space-y-6" data-testid="customer-loyalty-tab">
      {/* 1) Tier progress hero */}
      <div className={`relative rounded-3xl border-2 ${tv.ring} bg-white overflow-hidden`} data-testid="loyalty-tier-card">
        <div className={`absolute inset-x-0 top-0 h-32 bg-gradient-to-br ${tv.grad} opacity-20 pointer-events-none`} />
        <div className="relative p-6 sm:p-8">
          <div className="flex items-start gap-4">
            <div className={`h-16 w-16 rounded-2xl bg-gradient-to-br ${tv.grad} text-white flex items-center justify-center shadow-lg shadow-amber-500/30 shrink-0`}>
              <TierIcon className="h-8 w-8" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-widest font-bold text-neutral-500">RentalCar.al Member</div>
              <h2 className="font-display text-3xl font-semibold leading-tight mt-0.5" data-testid="loyalty-tier-name">
                {tier.label} tier
              </h2>
              <div className="text-sm text-neutral-600 mt-1">
                <Star className="inline h-3.5 w-3.5 text-amber-500 mr-1" />
                Earning <strong>{tier.earn_mult.toFixed(1)}×</strong> points on every confirmed trip
              </div>
            </div>
            <div className="hidden sm:block text-right">
              <div className="text-[10px] uppercase tracking-widest font-bold text-neutral-500">Lifetime</div>
              <div className="text-lg font-semibold">{data.lifetime_trip_count} {data.lifetime_trip_count === 1 ? "trip" : "trips"}</div>
              <div className="text-xs text-neutral-500">€{data.lifetime_spend_eur.toFixed(2)} spent</div>
            </div>
          </div>

          {progress ? (
            <div className="mt-6" data-testid="loyalty-progress">
              <div className="flex items-baseline justify-between mb-2">
                <span className="text-xs text-neutral-500">Progress to <strong className="text-neutral-700">{progress.next_tier}</strong></span>
                <span className="text-xs font-bold text-neutral-700">{pct}%</span>
              </div>
              <div className="h-2.5 w-full rounded-full bg-neutral-100 overflow-hidden">
                <div className={`h-full bg-gradient-to-r ${tv.grad} transition-all duration-500`}
                     style={{ width: `${pct}%` }} />
              </div>
              <div className="mt-2 flex flex-wrap gap-2 text-[11px]">
                {progress.trips_to_next > 0 && (
                  <span className="inline-flex items-center gap-1 text-neutral-500" data-testid="loyalty-trips-to-next">
                    <Flame className="h-3 w-3 text-orange-500" />
                    {progress.trips_to_next} more {progress.trips_to_next === 1 ? "trip" : "trips"}
                  </span>
                )}
                {progress.spend_to_next > 0 && (
                  <span className="inline-flex items-center gap-1 text-neutral-500">
                    <Coins className="h-3 w-3 text-amber-500" />
                    or €{progress.spend_to_next.toFixed(2)} more spend
                  </span>
                )}
              </div>
            </div>
          ) : (
            <div className="mt-6 inline-flex items-center gap-2 text-sm text-yellow-700 font-semibold" data-testid="loyalty-max-tier">
              <Crown className="h-4 w-4" /> You've reached the top tier — enjoy your Gold perks.
            </div>
          )}
        </div>
      </div>

      {/* 2 + 3) Points balance + Perks */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 rounded-3xl border border-emerald-100 bg-gradient-to-br from-emerald-50/50 via-white to-teal-50/30 p-6" data-testid="loyalty-points-card">
          <div className="flex items-center gap-2 mb-2">
            <div className="h-9 w-9 rounded-xl bg-emerald-100 ring-1 ring-emerald-200 flex items-center justify-center">
              <Coins className="h-4 w-4 text-emerald-700" />
            </div>
            <span className="text-[10px] uppercase tracking-widest font-bold text-emerald-700">Points balance</span>
          </div>
          <div className="font-display text-4xl font-bold text-neutral-900" data-testid="loyalty-points-balance">
            {data.points.toLocaleString()}
          </div>
          <div className="text-sm text-neutral-600 mt-1">
            Worth <strong>€{data.points_value_eur.toFixed(2)}</strong> off your next booking
          </div>
          <div className="text-[11px] text-neutral-500 mt-3 leading-snug">
            Redeem at checkout — 100 pts = €1. Cap {Math.round(data.redeem_cap_pct * 100)}% of rental total per trip.
          </div>
        </div>

        <div className="lg:col-span-2 rounded-3xl border border-violet-100 bg-white p-6" data-testid="loyalty-perks-card">
          <div className="flex items-center gap-2 mb-4">
            <div className="h-9 w-9 rounded-xl bg-violet-100 ring-1 ring-violet-200 flex items-center justify-center">
              <Sparkles className="h-4 w-4 text-violet-700" />
            </div>
            <h3 className="font-display text-lg font-semibold">Your {tier.label} perks</h3>
            <span className={`ml-auto text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full border ${tv.chip}`}>
              {tier.label}
            </span>
          </div>
          <ul className="space-y-2.5">
            {tier.perks.map((p, i) => (
              <li key={`${tier.label}-perk-${i}-${typeof p === "string" ? p.slice(0, 24) : i}`} className="flex items-start gap-2.5 text-sm text-neutral-700" data-testid={`loyalty-perk-${i}`}>
                <Check className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
                <span>{p}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* 4) Referral share */}
      <div className="rounded-3xl border border-indigo-100 bg-gradient-to-br from-indigo-50/50 via-white to-violet-50/30 p-6" data-testid="loyalty-referral-card">
        <div className="flex items-start gap-3 mb-4">
          <div className="h-11 w-11 rounded-2xl bg-indigo-100 ring-1 ring-indigo-200 flex items-center justify-center shrink-0">
            <Gift className="h-5 w-5 text-indigo-700" />
          </div>
          <div className="flex-1">
            <h3 className="font-display text-lg font-semibold leading-tight">Invite friends, both get €10</h3>
            <p className="text-sm text-neutral-600 mt-0.5">
              Share your code. When a friend signs up and completes their first trip, you each earn 1,000 points (€10 off).
            </p>
          </div>
          {data.referrals_completed > 0 && (
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid="loyalty-referrals-count">
              <Users2 className="h-3 w-3" /> {data.referrals_completed} friend{data.referrals_completed === 1 ? "" : "s"} joined
            </span>
          )}
        </div>

        <div className="flex flex-wrap items-stretch gap-2 mb-4">
          <div className="flex-1 min-w-[200px] rounded-2xl border-2 border-dashed border-indigo-200 bg-white px-4 py-3 flex items-center gap-3">
            <div className="text-[10px] uppercase tracking-widest font-bold text-indigo-600">Your code</div>
            <div className="font-mono text-lg font-bold text-neutral-900 tracking-wider" data-testid="loyalty-referral-code">
              {data.referral_code}
            </div>
          </div>
          <Button
            onClick={copyCode}
            className="rounded-2xl h-auto bg-gradient-to-br from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 text-white px-5"
            data-testid="loyalty-copy-code-btn"
          >
            {copied ? <Check className="h-4 w-4 mr-2" /> : <Copy className="h-4 w-4 mr-2" />}
            {copied ? "Copied" : "Copy"}
          </Button>
        </div>

        <div className="flex flex-wrap gap-2">
          <a
            href={`https://wa.me/?text=${shareMsg}`}
            target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 transition"
            data-testid="loyalty-share-whatsapp"
          >
            <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
          </a>
          <a
            href={`mailto:?subject=${encodeURIComponent("Get €10 off RentalCar.al")}&body=${shareMsg}`}
            className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200 hover:bg-rose-100 transition"
            data-testid="loyalty-share-email"
          >
            <Mail className="h-3.5 w-3.5" /> Email
          </a>
          <a
            href={`sms:?&body=${shareMsg}`}
            className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-sky-50 text-sky-700 border border-sky-200 hover:bg-sky-100 transition"
            data-testid="loyalty-share-sms"
          >
            <Share2 className="h-3.5 w-3.5" /> SMS
          </a>
          <span className="text-[11px] text-neutral-500 self-center ml-1">
            Code resolves to <span className="font-mono">{referralUrl}</span>
          </span>
        </div>
      </div>

      {/* 5) Earnings ledger */}
      <div className="rounded-3xl border border-border bg-white p-6" data-testid="loyalty-ledger-card">
        <div className="flex items-center gap-2 mb-4">
          <div className="h-9 w-9 rounded-xl bg-amber-100 ring-1 ring-amber-200 flex items-center justify-center">
            <Receipt className="h-4 w-4 text-amber-700" />
          </div>
          <h3 className="font-display text-lg font-semibold">Earnings ledger</h3>
          <span className="ml-auto text-xs text-neutral-500">Last {transactions.length} transactions</span>
        </div>
        {transactions.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border p-8 text-center text-sm text-neutral-500" data-testid="loyalty-ledger-empty">
            <Sparkles className="h-6 w-6 text-neutral-400 mx-auto mb-2" />
            No transactions yet. Your first earnings will land here when you complete your first trip.
          </div>
        ) : (
          <ul className="divide-y divide-border" data-testid="loyalty-ledger-list">
            {transactions.map((tx) => {
              const cfg = TX_TONE[tx.type] || TX_TONE.adjust;
              const Icon = TX_ICON_LOOKUP[cfg.icon];
              const positive = tx.points >= 0;
              return (
                <li key={tx.id} className="flex items-center gap-3 py-2.5" data-testid={`loyalty-tx-${tx.id}`}>
                  <span className={`h-9 w-9 rounded-xl ${cfg.bg} ${cfg.text} ring-1 ${cfg.ring} flex items-center justify-center shrink-0`}>
                    <Icon className="h-4 w-4" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-neutral-800 truncate">
                      {tx.description || `${tx.type[0].toUpperCase()}${tx.type.slice(1)}`}
                    </div>
                    <div className="text-[11px] text-neutral-500">
                      {(tx.created_at || "").slice(0, 10)}
                      {tx.amount_eur ? ` · €${tx.amount_eur.toFixed(2)}` : ""}
                    </div>
                  </div>
                  <div className={`text-sm font-bold whitespace-nowrap ${positive ? "text-emerald-700" : "text-rose-700"}`}>
                    {positive ? "+" : ""}{tx.points.toLocaleString()} pts
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* 6) How it works */}
      <div className="rounded-3xl border border-border bg-gradient-to-br from-neutral-50 to-white p-6" data-testid="loyalty-how-it-works">
        <div className="flex items-center gap-2 mb-3">
          <BookOpen className="h-4 w-4 text-neutral-600" />
          <h3 className="font-display text-lg font-semibold">How it works</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
          <div className="rounded-2xl bg-emerald-50/50 border border-emerald-100 p-4">
            <div className="font-semibold text-emerald-800 mb-1 flex items-center gap-1">
              <Coins className="h-3.5 w-3.5" /> Earn
            </div>
            <p className="text-neutral-700 leading-snug">
              {data.earn_rate_per_eur} points for every €1 spent on a confirmed booking, multiplied by your tier bonus.
            </p>
          </div>
          <div className="rounded-2xl bg-violet-50/50 border border-violet-100 p-4">
            <div className="font-semibold text-violet-800 mb-1 flex items-center gap-1">
              <Trophy className="h-3.5 w-3.5" /> Level up
            </div>
            <p className="text-neutral-700 leading-snug">
              Complete 3 trips OR €500 spent → Silver. 10 trips OR €2,000 → Gold. Unlock perks instantly.
            </p>
          </div>
          <div className="rounded-2xl bg-indigo-50/50 border border-indigo-100 p-4">
            <div className="font-semibold text-indigo-800 mb-1 flex items-center gap-1">
              <BadgePercent className="h-3.5 w-3.5" /> Redeem
            </div>
            <p className="text-neutral-700 leading-snug">
              At checkout, apply up to {Math.round(data.redeem_cap_pct * 100)}% off the rental total. 100 pts = €1.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
